import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CadDrawingListComponent } from '../../cad-viewer/cad-drawing-list/cad-drawing-list.component';
import {
  LrsCadastralRecord,
  LrsCadastralRecordKind,
  LrsCadastralReport,
  LrsCadastralReportType,
  LrsCadastralSummary,
  LrsLandParcel,
  LrsLandParcelInput,
  LrsParcelStatus,
  LrsRoute,
  LrsService,
} from 'src/app/shared/services/lrs.service';

export type CadastralTab = 'parcels' | LrsCadastralRecordKind | 'reports';

type FieldType = 'text' | 'number' | 'date' | 'textarea' | 'km';

interface RecordFieldDef {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
}

interface RecordFormDef {
  label: string;
  statuses: string[];
  fields: RecordFieldDef[];
  /** Utility relocations are route-level; the parcel link is optional. */
  routeLevel?: boolean;
}

export const PARCEL_STATUS_META: Record<LrsParcelStatus, { label: string; color: string }> = {
  owned: { label: 'Owned', color: '#16a34a' },
  under_process: { label: 'Under process', color: '#eab308' },
  pending: { label: 'Pending', color: '#ef4444' },
  disputed: { label: 'Disputed', color: '#b91c1c' },
};

const RECORD_FORMS: Record<LrsCadastralRecordKind, RecordFormDef> = {
  compensation: {
    label: 'Compensation',
    statuses: ['draft', 'offered', 'accepted', 'paid'],
    fields: [
      { key: 'amount', label: 'Amount (₹)', type: 'number', required: true },
      { key: 'awarded_on', label: 'Awarded on', type: 'date' },
      { key: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
  },
  permissions: {
    label: 'Permissions',
    statuses: ['applied', 'granted', 'rejected', 'expired'],
    fields: [
      { key: 'permission_type', label: 'Permission type', type: 'text', required: true },
      { key: 'authority', label: 'Authority', type: 'text' },
      { key: 'valid_from', label: 'Valid from', type: 'date' },
      { key: 'valid_to', label: 'Valid to', type: 'date' },
      { key: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
  },
  disputes: {
    label: 'Disputes',
    statuses: ['open', 'hearing', 'resolved', 'dismissed'],
    fields: [
      { key: 'dispute_type', label: 'Dispute type', type: 'text', required: true },
      { key: 'filed_on', label: 'Filed on', type: 'date' },
      { key: 'resolution_notes', label: 'Resolution notes', type: 'textarea' },
    ],
  },
  'utility-relocations': {
    label: 'Utility relocation',
    statuses: ['planned', 'in_progress', 'completed'],
    routeLevel: true,
    fields: [
      { key: 'utility_type', label: 'Utility type', type: 'text', required: true },
      { key: 'agency', label: 'Agency', type: 'text' },
      { key: 'from_measure_m', label: 'From km', type: 'km' },
      { key: 'to_measure_m', label: 'To km', type: 'km' },
      { key: 'cost', label: 'Cost (₹)', type: 'number' },
      { key: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
  },
};

const EMPTY_PARCEL = (): LrsLandParcelInput & { id?: number } => ({
  parcel_code: '',
  survey_no: '',
  owner_name: '',
  status: 'pending',
  land_use: '',
  zoning: '',
  side: '',
  area_ha: null,
  remarks: '',
});

@Component({
  selector: 'app-lrs-cadastral-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, CadDrawingListComponent],
  templateUrl: './lrs-cadastral-panel.component.html',
  styleUrls: ['./lrs-cadastral-panel.component.css'],
})
export class LrsCadastralPanelComponent implements OnChanges {
  @Input() route: LrsRoute | null = null;
  /** Parcel picked on the map by the host. */
  @Input() selectedParcelId: number | null = null;
  /** Polygon drawn on the host map (EPSG:4326). */
  @Input() drawnGeometry: GeoJSON.Polygon | null = null;
  @Input() drawActive = false;
  @Input() canEdit = true;

  @Output() parcelsChange = new EventEmitter<LrsLandParcel[]>();
  @Output() parcelSelect = new EventEmitter<number | null>();
  @Output() drawToggle = new EventEmitter<boolean>();
  @Output() zoomToParcel = new EventEmitter<LrsLandParcel>();

  readonly tabs: { id: CadastralTab; label: string }[] = [
    { id: 'parcels', label: 'Land Parcels' },
    { id: 'compensation', label: 'Compensation' },
    { id: 'permissions', label: 'Permissions' },
    { id: 'utility-relocations', label: 'Utility Relocation' },
    { id: 'disputes', label: 'Disputes' },
    { id: 'reports', label: 'Land Reports' },
  ];
  readonly parcelStatuses = Object.keys(PARCEL_STATUS_META) as LrsParcelStatus[];
  readonly statusMeta = PARCEL_STATUS_META;
  readonly recordForms = RECORD_FORMS;
  readonly reportTypes: { id: LrsCadastralReportType; label: string }[] = [
    { id: 'summary', label: 'Acquisition summary' },
    { id: 'parcels', label: 'Parcel register' },
    { id: 'compensation', label: 'Compensation' },
    { id: 'disputes', label: 'Disputes' },
    { id: 'permissions', label: 'Permissions' },
    { id: 'utility-relocations', label: 'Utility relocation' },
  ];

  tab: CadastralTab = 'parcels';
  loading = false;
  message = '';
  error = '';

  parcels: LrsLandParcel[] = [];
  statusFilter: LrsParcelStatus | '' = '';
  summary: LrsCadastralSummary | null = null;

  parcelForm: (LrsLandParcelInput & { id?: number }) | null = null;
  geometryText = '';
  /** Values loaded into the edit form, to detect boundary/area changes on save. */
  private originalGeometryText = '';
  private originalAreaHa: number | null = null;

  records: LrsCadastralRecord[] = [];
  recordParcelFilter: number | null = null;
  recordForm: Record<string, unknown> | null = null;

  reportType: LrsCadastralReportType = 'summary';
  report: LrsCadastralReport | null = null;

  /** Selection this panel emitted itself, so the echoed input change doesn't reopen the form. */
  private selfSelectedId: number | null | undefined;

  constructor(private lrs: LrsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['route'] && this.route?.id !== changes['route'].previousValue?.id) {
      this.parcelForm = null;
      this.recordForm = null;
      this.report = null;
      this.recordParcelFilter = null;
      this.reload();
    }
    if (changes['selectedParcelId'] && !changes['selectedParcelId'].firstChange) {
      this.onExternalSelect(this.selectedParcelId);
    }
    if (changes['drawnGeometry'] && this.drawnGeometry) {
      if (!this.parcelForm) {
        this.parcelForm = EMPTY_PARCEL();
        this.originalGeometryText = '';
        this.originalAreaHa = null;
      }
      this.tab = 'parcels';
      this.geometryText = JSON.stringify(this.drawnGeometry);
    }
  }

  get routeProjectId(): number | null {
    const pid = Number(this.route?.project_id);
    return Number.isFinite(pid) && pid > 0 ? pid : null;
  }

  get selectedParcel(): LrsLandParcel | null {
    return this.parcels.find((p) => p.id === this.selectedParcelId) || null;
  }

  get filteredParcels(): LrsLandParcel[] {
    return this.statusFilter ? this.parcels.filter((p) => p.status === this.statusFilter) : this.parcels;
  }

  get activeRecordForm(): RecordFormDef | null {
    return this.isRecordTab(this.tab) ? RECORD_FORMS[this.tab] : null;
  }

  isRecordTab(tab: CadastralTab): tab is LrsCadastralRecordKind {
    return tab in RECORD_FORMS;
  }

  setTab(tab: CadastralTab): void {
    this.tab = tab;
    this.recordForm = null;
    this.error = '';
    this.message = '';
    if (this.isRecordTab(tab)) {
      if (this.recordParcelFilter == null && this.selectedParcelId) this.recordParcelFilter = this.selectedParcelId;
      this.loadRecords();
    } else if (tab === 'reports') {
      this.loadSummary();
      this.loadReport();
    }
  }

  reload(): void {
    if (!this.route) return;
    this.loadParcels();
    this.loadSummary();
    if (this.isRecordTab(this.tab)) this.loadRecords();
    if (this.tab === 'reports') this.loadReport();
  }

  private loadParcels(): void {
    if (!this.route) return;
    this.loading = true;
    this.lrs.listCadastralParcels(this.route.id).subscribe({
      next: (res) => {
        this.parcels = res.data || [];
        this.loading = false;
        this.parcelsChange.emit(this.parcels);
      },
      error: (e) => {
        this.loading = false;
        this.parcels = [];
        this.parcelsChange.emit([]);
        this.showError(e);
      },
    });
  }

  private loadSummary(): void {
    if (!this.route) return;
    this.lrs.getCadastralSummary(this.route.id).subscribe({
      next: (res) => (this.summary = res.data),
      error: () => (this.summary = null),
    });
  }

  // ---- Parcels ----

  selectParcel(p: LrsLandParcel): void {
    this.emitSelect(p.id);
    this.zoomToParcel.emit(p);
  }

  private emitSelect(id: number | null): void {
    this.selfSelectedId = id;
    this.parcelSelect.emit(id);
  }

  private onExternalSelect(id: number | null): void {
    if (id === this.selfSelectedId) {
      this.selfSelectedId = undefined;
      return;
    }
    this.selfSelectedId = undefined;
    if (id == null) return;
    const p = this.parcels.find((x) => x.id === id);
    if (!p) return;
    if (this.isRecordTab(this.tab)) {
      this.recordParcelFilter = id;
      this.loadRecords();
    } else if (this.tab === 'parcels' && this.canEdit) {
      this.editParcel(p);
    }
  }

  newParcel(): void {
    this.parcelForm = EMPTY_PARCEL();
    this.parcelForm.parcel_code = this.nextParcelCode();
    this.geometryText = '';
    this.originalGeometryText = '';
    this.originalAreaHa = null;
    this.error = '';
  }

  editParcel(p: LrsLandParcel): void {
    this.parcelForm = {
      id: p.id,
      parcel_code: p.parcel_code,
      survey_no: p.survey_no || '',
      owner_name: p.owner_name || '',
      status: p.status,
      land_use: p.land_use || '',
      zoning: p.zoning || '',
      side: p.side || '',
      area_ha: p.area_ha ?? null,
      remarks: p.remarks || '',
    };
    this.geometryText = p.geometry ? JSON.stringify(p.geometry) : '';
    this.originalGeometryText = this.geometryText;
    this.originalAreaHa = p.area_ha ?? null;
    this.error = '';
  }

  cancelParcel(): void {
    this.parcelForm = null;
    this.geometryText = '';
    if (this.drawActive) this.drawToggle.emit(false);
  }

  toggleDraw(): void {
    this.drawToggle.emit(!this.drawActive);
  }

  saveParcel(): void {
    if (!this.route || !this.parcelForm) return;
    const f = this.parcelForm;
    if (!String(f.parcel_code || '').trim()) {
      this.error = 'Parcel code is required';
      return;
    }
    const geomText = this.geometryText.trim();
    const geometryChanged = geomText !== this.originalGeometryText.trim();
    let geometry: unknown;
    if (geomText && geometryChanged) {
      try {
        geometry = JSON.parse(geomText);
      } catch {
        this.error = 'Geometry is not valid GeoJSON';
        return;
      }
    } else if (!geomText && geometryChanged) {
      geometry = null;
    }
    let areaHa = f.area_ha === null || (f.area_ha as unknown) === '' ? null : Number(f.area_ha);
    // A new boundary with an untouched area should be re-measured server-side.
    if (geometry && areaHa === this.originalAreaHa) areaHa = null;
    const body: LrsLandParcelInput = {
      parcel_code: f.parcel_code.trim(),
      survey_no: f.survey_no || null,
      owner_name: f.owner_name || null,
      status: f.status,
      land_use: f.land_use || null,
      zoning: f.zoning || null,
      side: f.side || null,
      area_ha: areaHa,
      remarks: f.remarks || null,
    };
    if (geometry !== undefined) body.geometry = geometry;
    const req = f.id
      ? this.lrs.updateCadastralParcel(f.id, body)
      : this.lrs.createCadastralParcel(this.route.id, body);
    req.subscribe({
      next: (res) => {
        this.message = `Parcel ${res.data.parcel_code} saved`;
        this.error = '';
        this.parcelForm = null;
        this.geometryText = '';
        if (this.drawActive) this.drawToggle.emit(false);
        this.emitSelect(res.data.id);
        this.reload();
      },
      error: (e) => this.showError(e),
    });
  }

  deleteParcel(p: { id?: number; parcel_code: string }): void {
    if (!p.id || !confirm(`Delete parcel ${p.parcel_code}? Linked records stay in history.`)) return;
    this.lrs.deleteCadastralParcel(p.id).subscribe({
      next: () => {
        this.message = `Parcel ${p.parcel_code} deleted`;
        this.parcelForm = null;
        if (this.selectedParcelId === p.id) this.emitSelect(null);
        this.reload();
      },
      error: (e) => this.showError(e),
    });
  }

  private nextParcelCode(): string {
    const nums = this.parcels
      .map((p) => Number(String(p.parcel_code).replace(/\D+/g, '')))
      .filter((n) => Number.isFinite(n) && n > 0);
    const next = nums.length ? Math.max(...nums) + 1 : 1;
    return `LP-${String(next).padStart(4, '0')}`;
  }

  // ---- Records ----

  loadRecords(): void {
    if (!this.route || !this.isRecordTab(this.tab)) return;
    this.lrs.listCadastralRecords(this.route.id, this.tab, this.recordParcelFilter).subscribe({
      next: (res) => (this.records = res.data || []),
      error: (e) => {
        this.records = [];
        this.showError(e);
      },
    });
  }

  newRecord(): void {
    const def = this.activeRecordForm;
    if (!def) return;
    const form: Record<string, unknown> = {
      status: def.statuses[0],
      parcel_id: this.recordParcelFilter ?? this.selectedParcelId ?? null,
    };
    for (const fd of def.fields) form[fd.key] = '';
    this.recordForm = form;
    this.error = '';
  }

  editRecord(r: LrsCadastralRecord): void {
    const def = this.activeRecordForm;
    if (!def) return;
    const form: Record<string, unknown> = { id: r.id, status: r.status, parcel_id: r.parcel_id ?? null };
    for (const fd of def.fields) {
      const v = r[fd.key];
      if (fd.type === 'date') form[fd.key] = v ? String(v).slice(0, 10) : '';
      else if (fd.type === 'km') form[fd.key] = v != null ? Number(v) / 1000 : '';
      else form[fd.key] = v ?? '';
    }
    this.recordForm = form;
    this.error = '';
  }

  saveRecord(): void {
    const def = this.activeRecordForm;
    const form = this.recordForm;
    if (!this.route || !def || !form || !this.isRecordTab(this.tab)) return;
    const kind = this.tab;
    for (const fd of def.fields) {
      if (fd.required && (form[fd.key] === '' || form[fd.key] == null)) {
        this.error = `${fd.label} is required`;
        return;
      }
    }
    const body: Record<string, unknown> = { status: form['status'] };
    for (const fd of def.fields) {
      const v = form[fd.key];
      if (v === '' || v == null) body[fd.key] = null;
      else if (fd.type === 'km') body[fd.key] = Number(v) * 1000;
      else if (fd.type === 'number') body[fd.key] = Number(v);
      else body[fd.key] = v;
    }
    const parcelId = form['parcel_id'] != null && form['parcel_id'] !== '' ? Number(form['parcel_id']) : null;
    if (def.routeLevel) body['parcel_id'] = parcelId;

    const id = form['id'] as number | undefined;
    let req;
    if (id) {
      req = this.lrs.updateCadastralRecord(kind, id, body);
    } else if (kind === 'utility-relocations') {
      req = this.lrs.createUtilityRelocation(this.route.id, body);
    } else {
      if (!parcelId) {
        this.error = 'Select a parcel first';
        return;
      }
      req = this.lrs.createParcelRecord(parcelId, kind, body);
    }
    req.subscribe({
      next: () => {
        this.message = `${def.label} saved`;
        this.error = '';
        this.recordForm = null;
        this.loadRecords();
        this.loadParcels();
        this.loadSummary();
      },
      error: (e) => this.showError(e),
    });
  }

  deleteRecord(r: LrsCadastralRecord): void {
    if (!this.isRecordTab(this.tab) || !confirm('Delete this record?')) return;
    const kind = this.tab;
    this.lrs.deleteCadastralRecord(kind, r.id).subscribe({
      next: () => {
        this.message = 'Record deleted';
        this.loadRecords();
        this.loadParcels();
        this.loadSummary();
      },
      error: (e) => this.showError(e),
    });
  }

  displayValue(r: LrsCadastralRecord, fd: RecordFieldDef): string {
    const v = r[fd.key];
    if (v == null || v === '') return '—';
    if (fd.type === 'date') return String(v).slice(0, 10);
    if (fd.type === 'km') return (Number(v) / 1000).toFixed(3);
    if (fd.key === 'amount' || fd.key === 'cost') return this.inr(Number(v));
    return String(v);
  }

  tableFields(def: RecordFormDef): RecordFieldDef[] {
    return def.fields.filter((f) => f.type !== 'textarea');
  }

  // ---- Reports ----

  loadReport(): void {
    if (!this.route) return;
    this.lrs.getCadastralReport(this.route.id, this.reportType).subscribe({
      next: (res) => (this.report = res.data),
      error: (e) => {
        this.report = null;
        this.showError(e);
      },
    });
  }

  exportCsv(): void {
    if (!this.route) return;
    const routeId = this.route.id;
    const type = this.reportType;
    this.lrs.downloadCadastralReportCsv(routeId, type).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `land-${type}-route-${routeId}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (e) => this.showError(e),
    });
  }

  statusCount(map: Record<string, { count: number }> | undefined, statuses: string[]): number {
    if (!map) return 0;
    return statuses.reduce((s, k) => s + (map[k]?.count || 0), 0);
  }

  // ---- Formatting ----

  inr(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(Number(n))) return '—';
    return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }

  km(m: number | null | undefined): string {
    return m == null ? '—' : (Number(m) / 1000).toFixed(3);
  }

  label(s: string): string {
    return s.replace(/[_-]+/g, ' ');
  }

  private showError(e: unknown): void {
    const err = e as { error?: { error?: string; message?: string }; message?: string };
    this.error = err?.error?.error || err?.error?.message || err?.message || 'Request failed';
    this.message = '';
  }
}
